import {
  useAssignSetting,
  useSetting,
} from "@faire/retailer-visitor-shared/settings/__internal__/useSetting";
import { SettingConst } from "@faire/web-api--source/types";
import { IFrontendSetting } from "@faire/web/common/settings/declarations/IFrontendSetting";
import {
  FrontendSettingName,
  NullableSettingType,
} from "@faire/web/common/settings/declarations/ISettingSchema";
import { useEffect } from "react";

import { useSettingReviewsHoldoutXP } from "./useSettingReviewsHoldoutXP";

type SettingDefinedInBE<T> = SettingConst<
  string,
  T,
  | "RETAILER"
  | "LOGGED_IN_RETAILER"
  | "RETAILER_ANDROID"
  | "RETAILER_IOS"
  | "VISITOR_WEB"
  | "HOTJAR_RETAILER"
>;

type SettingDefinedInFE<T extends NullableSettingType> = IFrontendSetting<
  T,
  FrontendSettingName,
  "RETAILER" | "LOGGED_IN_RETAILER" | "VISITOR_WEB"
>;

const useWithReviewsHoldoutGating = <T>(value: T, defaultValue: T) => {
  const isInHoldout = useSettingReviewsHoldoutXP();
  if (isInHoldout) {
    return defaultValue;
  }
  return value;
};

export const useReviewsHoldoutGatedSetting = <T>(
  settingOrKey: SettingDefinedInBE<T>,
  defaultValue: T
) => {
  const settingValue = useSetting(settingOrKey, defaultValue);
  return useWithReviewsHoldoutGating(settingValue, defaultValue);
};

export const useReviewsHoldoutGatedFESetting = <T extends NullableSettingType>(
  settingOrKey: SettingDefinedInFE<T>,
  defaultValue: T
) => {
  const settingValue = useSetting(settingOrKey);
  return useWithReviewsHoldoutGating(settingValue, defaultValue);
};

export const useReviewsHoldoutGatedAssignSettingEffect = <
  T extends NullableSettingType,
>(
  settingOrKey: SettingDefinedInBE<T> | SettingDefinedInFE<T>,
  additionalConditionsForAssignmentMet: boolean = true
) => {
  const isInHoldout = useSettingReviewsHoldoutXP();
  const assignSetting = useAssignSetting(settingOrKey);

  useEffect(() => {
    if (!isInHoldout && additionalConditionsForAssignmentMet) {
      assignSetting();
    }
  }, [assignSetting, isInHoldout, additionalConditionsForAssignmentMet]);
};
